import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Delete from "../../assets/images/delete.svg";
import EditIc from "../../assets/images/ic-edit.svg";
import ApiConfig from "../../api/ApiConfig";
import {
  simplePostCall,
  simplePUTCall,
  simpleDeleteCall,
  getWithAuthCall,
} from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import { DateDDMMYYYY } from "../../sharedComponent/common";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { PageHead, StatusBadge } from "./ffUi";
import "./FleetFinance.scss";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

// Postgres NUMERIC may arrive as strings ("500.00"); coerce before formatting.
const money = (n) =>
  n || n === 0 ? Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 }) : "--";

const emptyForm = {
  vehicle_id: "",
  valuation_date: "",
  baseline_value: "",
  condition_rating: 3,
  salvage_value: "",
  disposal_cost: "",
  notes: "",
};

const AssetValuation = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [valuations, setValuations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filterVehicle, setFilterVehicle] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // condition scale reference
  const [scale, setScale] = useState([]);
  const [showScale, setShowScale] = useState(false);

  // impairment check
  const [showImpair, setShowImpair] = useState(false);
  const [impairForm, setImpairForm] = useState({ vehicle_id: "", fmv_value: "", post: false });
  const [impairResult, setImpairResult] = useState(null);
  const [impairing, setImpairing] = useState(false);

  useEffect(() => {
    getVehicles();
    getConditionScale();
  }, []);

  useEffect(() => {
    getValuations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterVehicle]);

  const getVehicles = () => {
    getWithAuthCall(ApiConfig.VEHICEL_AVALIVALITY_DROPDAWAN)
      .then((data) => {
        if (data?.data) setVehicles(data.data);
      })
      .catch((err) => console.log("vehicle dropdown err", err));
  };

  const getConditionScale = () => {
    getWithAuthCall(ApiConfig.FLEET_FINANCE_VALUATION_CONDITION_SCALE)
      .then((data) => {
        if (data?.success || data?.result) setScale(data?.data || []);
      })
      .catch((err) => console.log("condition scale err", err));
  };

  const getValuations = () => {
    setLoading(true);
    const body = JSON.stringify({
      page: 1,
      page_limit: 50,
      vehicle_id: filterVehicle ? Number(filterVehicle) : null,
    });
    simplePostCall(ApiConfig.FLEET_FINANCE_VALUATION_LIST, body)
      .then((data) => {
        setValuations((data?.success || data?.result) ? data?.data || [] : []);
      })
      .catch((err) => {
        console.log("valuation list err", err);
        setValuations([]);
      })
      .finally(() => setLoading(false));
  };

  const openAdd = () => {
    setIsEdit(false);
    setForm({ ...emptyForm, vehicle_id: filterVehicle || "" });
    setShowForm(true);
  };

  const openEdit = (v) => {
    setIsEdit(true);
    setForm({
      valuation_id: v.valuation_id,
      vehicle_id: v.vehicle_id || "",
      valuation_date: v.valuation_date ? String(v.valuation_date).slice(0, 10) : "",
      baseline_value: v.baseline_value ?? "",
      condition_rating: v.condition_rating ?? 3,
      salvage_value: v.salvage_value ?? "",
      disposal_cost: v.disposal_cost ?? "",
      notes: v.notes || "",
    });
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    if (isEdit) {
      const body = JSON.stringify({
        valuation_id: form.valuation_id,
        valuation_date: form.valuation_date,
        baseline_value: Number(form.baseline_value) || 0,
        condition_rating: Number(form.condition_rating),
        salvage_value: Number(form.salvage_value) || 0,
        disposal_cost: Number(form.disposal_cost) || 0,
        notes: form.notes,
      });
      simplePUTCall(ApiConfig.FLEET_FINANCE_VALUATION_UPDATE, body)
        .then(onSaved)
        .catch(onSaveErr)
        .finally(() => setSaving(false));
    } else {
      const body = JSON.stringify({
        vehicle_id: Number(form.vehicle_id),
        valuation_date: form.valuation_date,
        baseline_value: Number(form.baseline_value) || 0,
        condition_rating: Number(form.condition_rating),
        salvage_value: Number(form.salvage_value) || 0,
        disposal_cost: Number(form.disposal_cost) || 0,
        notes: form.notes,
      });
      simplePostCall(ApiConfig.FLEET_FINANCE_VALUATION_ADD, body)
        .then(onSaved)
        .catch(onSaveErr)
        .finally(() => setSaving(false));
    }
  };

  const onSaved = (data) => {
    if (data?.success || data?.result) {
      notifySuccess(data?.message || (isEdit ? "Valuation updated" : "Valuation added"));
      setShowForm(false);
      getValuations();
    } else {
      notifyError(data?.message || "Unable to save valuation");
    }
  };

  const onSaveErr = (err) => {
    console.log("save valuation err", err);
    notifyError("Something went wrong");
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setDeleteModal(true);
  };

  const handleDelete = () => {
    simpleDeleteCall(
      ApiConfig.FLEET_FINANCE_VALUATION_DELETE + deleteId,
      JSON.stringify({})
    )
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Valuation deleted");
          getValuations();
        } else {
          notifyError(data?.message || "Unable to delete valuation");
        }
      })
      .catch((err) => {
        console.log("delete valuation err", err);
        notifyError("Something went wrong");
      })
      .finally(() => {
        setDeleteModal(false);
        setDeleteId(null);
      });
  };

  const openImpair = () => {
    setImpairForm({ vehicle_id: filterVehicle || "", fmv_value: "", post: false });
    setImpairResult(null);
    setShowImpair(true);
  };

  const runImpairment = () => {
    if (!impairForm.vehicle_id) return notifyError("Select a vehicle first");
    setImpairing(true);
    setImpairResult(null);
    const body = JSON.stringify({
      vehicle_id: Number(impairForm.vehicle_id),
      ...(impairForm.fmv_value !== "" ? { fmv_value: Number(impairForm.fmv_value) } : {}),
      post: !!impairForm.post,
    });
    simplePostCall(ApiConfig.FLEET_FINANCE_VALUATION_IMPAIRMENT_CHECK, body)
      .then((data) => {
        if (data?.success || data?.result) {
          setImpairResult(data?.data || {});
          if (impairForm.post) {
            notifySuccess(data?.message || "Impairment journal posted");
            getValuations();
          }
        } else {
          notifyError(data?.message || "Unable to run impairment check");
        }
      })
      .catch((err) => {
        console.log("impairment err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setImpairing(false));
  };

  const vehicleLabel = (id) => {
    const v = vehicles.find((x) => String(x.vehicle_id) === String(id));
    return v ? v.vehicle_number || v.vehicle_name || v.vehicle_id : id;
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Valuation">
        <PageHead
          title={t("Asset Valuation")}
          subtitle={t("FMV / OLV / FLV valuations & IAS 36 impairment")}
        >
          <div className="form_input_main" style={{ width: 220, marginBottom: 0 }}>
            <Form.Select
              className="innerCust"
              value={filterVehicle}
              onChange={(e) => setFilterVehicle(e.target.value)}
            >
              <option value="">{t("All Vehicles")}</option>
              {vehicles.map((v) => (
                <option key={v.vehicle_id} value={v.vehicle_id}>
                  {v.vehicle_number || v.vehicle_name || v.vehicle_id}
                </option>
              ))}
            </Form.Select>
          </div>
          <button className="cx-btn-1" onClick={() => setShowScale(true)}>
            {t("Condition Scale")}
          </button>
          <button className="cx-btn-1" onClick={openImpair}>
            {t("Impairment Check")}
          </button>
          <button className="cx-btn-2" onClick={openAdd}>
            + {t("Add Valuation")}
          </button>
        </PageHead>

        {loading ? (
          <Loader />
        ) : (
          <div className="main-master-wrapper">
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Date")}</th>
                      <th>{t("Vehicle")}</th>
                      <th>{t("FMV")}</th>
                      <th>{t("OLV")}</th>
                      <th>{t("FLV")}</th>
                      <th>{t("NBV")}</th>
                      <th>{t("Condition")}</th>
                      <th>{t("Impaired")}</th>
                      <th>{t("Action")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {valuations && valuations.length > 0 ? (
                      valuations.map((v) => (
                        <tr key={v.valuation_id}>
                          <td>
                            {v.valuation_date ? DateDDMMYYYY(v.valuation_date) : "-"}
                          </td>
                          <td>{vehicleLabel(v.vehicle_id)}</td>
                          <td>{money(v.fmv_value)}</td>
                          <td>{money(v.olv_value)}</td>
                          <td>{money(v.flv_value)}</td>
                          <td>{money(v.net_book_value)}</td>
                          <td>{v.condition_rating ?? "-"}</td>
                          <td>
                            <StatusBadge
                              value={
                                (v.impairment_flag ?? v.is_impaired) ? "Impaired" : "OK"
                              }
                            />
                          </td>
                          <td>
                            <div className="d-flex">
                              <img
                                src={EditIc}
                                alt="edit"
                                style={{ cursor: "pointer", marginRight: 12 }}
                                onClick={() => openEdit(v)}
                              />
                              <img
                                src={Delete}
                                alt="delete"
                                style={{ cursor: "pointer" }}
                                onClick={() => confirmDelete(v.valuation_id)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        centered
        className="common-model"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {isEdit ? t("Edit Valuation") : t("Add Valuation")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Vehicle")} <span>&#42;</span>
                </Form.Label>
                <Form.Select
                  required
                  name="vehicle_id"
                  value={form.vehicle_id}
                  onChange={handleChange}
                  disabled={isEdit}
                >
                  <option value="">{t("Select Vehicle")}</option>
                  {vehicles.map((v) => (
                    <option key={v.vehicle_id} value={v.vehicle_id}>
                      {v.vehicle_number || v.vehicle_name || v.vehicle_id}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Valuation Date")} <span>&#42;</span>
                </Form.Label>
                <Form.Control
                  required
                  type="date"
                  name="valuation_date"
                  value={form.valuation_date}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Baseline Value")} <span>&#42;</span>
                </Form.Label>
                <Form.Control
                  required
                  type="number"
                  name="baseline_value"
                  value={form.baseline_value}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Condition Rating")}
                </Form.Label>
                <Form.Select
                  name="condition_rating"
                  value={form.condition_rating}
                  onChange={handleChange}
                >
                  {(scale.length
                    ? scale
                    : [1, 2, 3, 4, 5].map((r) => ({ rating: r, label: `Rating ${r}` }))
                  ).map((s) => (
                    <option key={s.rating} value={s.rating}>
                      {s.rating} - {s.label || s.condition || s.description || ""}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">{t("Salvage Value")}</Form.Label>
                <Form.Control
                  type="number"
                  name="salvage_value"
                  value={form.salvage_value}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">{t("Disposal Cost")}</Form.Label>
                <Form.Control
                  type="number"
                  name="disposal_cost"
                  value={form.disposal_cost}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-12 form_input_main">
                <Form.Label className="common-labels">{t("Notes")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="pop-up-modal-footer">
            <div className="btn-wrapper">
              <button
                type="button"
                className="cx-btn-1"
                onClick={() => setShowForm(false)}
              >
                {t("Cancel")}
              </button>
              <button type="submit" className="cx-btn-2" disabled={saving}>
                {saving ? <Spinner animation="border" size="sm" /> : t("Save")}
              </button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Condition scale modal */}
      <Modal
        show={showScale}
        onHide={() => setShowScale(false)}
        centered
        className="common-model ff-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Condition Rating Scale")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="VA_table">
            <table className="table tableAdmin">
              <thead className="tableHead">
                <tr>
                  <th>{t("Rating")}</th>
                  <th>{t("Condition")}</th>
                  <th>{t("Adjustment %")}</th>
                </tr>
              </thead>
              <tbody className="tableBody">
                {scale.length > 0 ? (
                  scale.map((s) => (
                    <tr key={s.rating}>
                      <td>{s.rating}</td>
                      <td>{s.label || s.condition || s.description || "-"}</td>
                      <td>{s.adjustment_pct ?? s.condition_adjustment_pct ?? "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>
                      <NoDataComp />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>

      {/* Impairment check modal */}
      <Modal
        show={showImpair}
        onHide={() => setShowImpair(false)}
        centered
        className="common-model"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Impairment Check (IAS 36)")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6 form_input_main">
              <Form.Label className="common-labels">
                {t("Vehicle")} <span>&#42;</span>
              </Form.Label>
              <Form.Select
                value={impairForm.vehicle_id}
                onChange={(e) =>
                  setImpairForm((p) => ({ ...p, vehicle_id: e.target.value }))
                }
              >
                <option value="">{t("Select Vehicle")}</option>
                {vehicles.map((v) => (
                  <option key={v.vehicle_id} value={v.vehicle_id}>
                    {v.vehicle_number || v.vehicle_name || v.vehicle_id}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-6 form_input_main">
              <Form.Label className="common-labels">
                {t("FMV Value")} ({t("optional")})
              </Form.Label>
              <Form.Control
                type="number"
                placeholder={t("Latest stored valuation")}
                value={impairForm.fmv_value}
                onChange={(e) =>
                  setImpairForm((p) => ({ ...p, fmv_value: e.target.value }))
                }
              />
            </div>
            <div className="col-md-12 form_input_main">
              <Form.Check
                type="checkbox"
                label={t("Post impairment journal (Dr 7040 / Cr 1512)")}
                checked={impairForm.post}
                onChange={(e) =>
                  setImpairForm((p) => ({ ...p, post: e.target.checked }))
                }
              />
            </div>
          </div>
          {impairResult && (
            <div
              style={{
                border: "0.5px solid #f6efe9",
                borderRadius: 12,
                padding: 12,
                marginTop: 8,
              }}
            >
              {Object.entries(impairResult).map(([k, v]) => (
                <div
                  key={k}
                  className="d-flex justify-content-between"
                  style={{ padding: "4px 0", color: "#703c19" }}
                >
                  <span>{k.replace(/_/g, " ")}</span>
                  <b>{typeof v === "object" ? JSON.stringify(v) : String(v)}</b>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="pop-up-modal-footer">
          <div className="btn-wrapper">
            <button
              type="button"
              className="cx-btn-1"
              onClick={() => setShowImpair(false)}
            >
              {t("Close")}
            </button>
            <button className="cx-btn-2" onClick={runImpairment} disabled={impairing}>
              {impairing ? <Spinner animation="border" size="sm" /> : t("Run Check")}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Delete modal */}
      <Modal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        centered
        className="common-model"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Delete Valuation")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("Are you sure you want to delete this valuation")} ?
        </Modal.Body>
        <Modal.Footer className="pop-up-modal-footer">
          <div className="btn-wrapper">
            <button className="cx-btn-1" onClick={() => setDeleteModal(false)}>
              {t("Cancel")}
            </button>
            <button className="cx-btn-2" onClick={handleDelete}>
              {t("Yes")}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
};

export default AssetValuation;
